import React, { ReactElement } from 'react'
import { FormControl, FormHelperText, Input, InputLabel } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

const FormInput = ({
  className,
  disabled,
  helperText,
  inputProps,
  multiline,
  name,
  onChangeText,
  placeholder,
  title,
  titleProps,
  useStyles: applyContainerStyles,
  value,
}: Props): ReactElement => {
  const { classes, cx } = useStyles()

  // Adornments belong on the Input itself. `inputProps` is spread onto the
  // native <input> element, which React rejects them from.
  const { startAdornment, endAdornment, ...nativeInputProps } = inputProps || {}

  return (
    <FormControl
      className={cx(
        className,
        applyContainerStyles !== false && classes.container,
      )}
    >
      {title && (
        <InputLabel htmlFor={title + '-input'} {...titleProps}>
          {title}
        </InputLabel>
      )}
      <Input
        aria-describedby={helperText ? title + '-helper-text' : undefined}
        disabled={disabled || nativeInputProps?.disabled}
        endAdornment={endAdornment}
        id={title + '-input'}
        inputProps={nativeInputProps}
        multiline={multiline}
        name={name}
        onChange={(e) => onChangeText(e.target.value, e)}
        placeholder={placeholder}
        startAdornment={startAdornment}
        value={value}
      />
      {helperText && (
        <FormHelperText id={title + '-helper-text'}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default FormInput

type Props = {
  className?: string
  disabled?: boolean
  helperText?: string
  /**
   * Props for the Input itself. `startAdornment` / `endAdornment` are lifted
   * onto the Input; everything else is spread onto the native <input>.
   */
  inputProps?: Record<string, any>
  multiline?: boolean
  name?: string
  /**
   * Triggered when the text changes
   */
  onChangeText: (text: string, event?: any) => void
  placeholder?: string
  title?: string
  titleProps?: Record<string, any>
  /**
   * Set to false to opt out of the default container styling.
   */
  useStyles?: boolean
  value?: any
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    flex: 1,
  },
}))
